<template>
    <div>
      <div class="action-bar">

        <template v-if="extraProp">
           <div class="row">
            <div
              v-if="selectedLocation && selectedLocation.gestiune_start_sync_order"
              class="info-note"
            >
              <span class="info-title">ℹ️ Notă informativă</span>
              <p>
                Vânzările pentru locația <strong>{{ selectedLocation.name }}</strong> apar doar începând cu data:
                <strong>{{ formatDate(null, null, selectedLocation.gestiune_start_sync_order) }}</strong>
              </p>
            </div>
          </div>
          <div class="row">
            <div class="action-item">
              <label>{{ $t('gestiune.locatie') }}</label>
              <select class="form-control" v-model="selectedLocation" :disabled="loading">
                <optgroup
                  v-for="group in groupedLocations"
                  :key="group.vat"
                  :label="`VAT: ${group.vat}`"
                >
                  <option v-for="loc in group.locations" :key="loc.id" :value="loc">
                    {{ loc.name }}
                  </option>
                </optgroup>
              </select>
            </div>

            <div class="action-item" style="
                display: flex;
                flex-direction: column;
            ">
              <label>{{ $t('gestiune.gestiune') }}</label>
              <el-select
                v-model="selectedGestiune"
                placeholder="Selectează gestiunea"
                :disabled="loading"
                class="w-100"
              >
                <el-option
                  v-for="gestiune in filteredGestiuni"
                  :key="gestiune.id"
                  :label="gestiune.name"
                  :value="gestiune.id"
                ></el-option>
              </el-select>
            </div>

            <div class="action-item">
              <label>Date și oră</label>
              <el-date-picker
                type="datetimerange"
                v-model="searchDate"
                format="yyyy-MM-dd HH:mm"
                value-format="yyyy-MM-dd HH:mm:ss"
                class="w-100"
                :picker-options="pickerOptions"
                :name="['date_from', 'date_to']"
                :disabled="loading || stocLaZi"
              ></el-date-picker>
            </div>

            <div class="search-button-reports">
                <button
                    :title="$t('reports.search')"
                    class="btn add mar-rt-10"
                    @click="triggerFilter"
                >
                    <i class="ti-search"></i>
                    <span>{{ $t('general.search') }}</span>
                </button>
            </div>
          </div>
        </template>

        <template v-else>
          <!-- Primul rând: locație, gestiune, produs, categorie, tip document, număr document -->
          <div class="row">
            <div
              v-if="selectedLocation && selectedLocation.gestiune_start_sync_order"
              class="info-note"
            >
              <span class="info-title">ℹ️ Notă informativă</span>
              <p>
                Vânzările pentru locația <strong>{{ selectedLocation.name }}</strong> apar doar începând cu data:
                <strong>{{ formatDate(null, null, selectedLocation.gestiune_start_sync_order) }}</strong>
              </p>
            </div>
          </div>

          <div class="row">
            <div class="action-item">
              <label>{{ $t('gestiune.locatie') }}</label>
              <select class="form-control" v-model="selectedLocation" :disabled="loading">
                <optgroup
                  v-for="group in groupedLocations"
                  :key="group.vat"
                  :label="`VAT: ${group.vat}`"
                >
                  <option v-for="loc in group.locations" :key="loc.id" :value="loc">
                    {{ loc.name }}
                  </option>
                </optgroup>
              </select>
            </div>

            <div class="action-item">
              <label>{{ $t('gestiune.gestiune') }}</label>
              <el-select
                v-model="selectedGestiune"
                placeholder="Selectează gestiunea"
                :disabled="loading"
                class="w-100"
              >
                <el-option
                  v-for="gestiune in filteredGestiuni"
                  :key="gestiune.id"
                  :label="gestiune.name"
                  :value="gestiune.id"
                ></el-option>
              </el-select>
            </div>

            <div class="action-item">
              <label>{{ $t('gestiune.categoria') }}</label>
              <select v-model="selectedCategory" class="form-control" :disabled="loading">
                <option
                  v-for="category in categories"
                  :key="category.value"
                  :value="category.value"
                >
                  {{ category.label }}
                </option>
              </select>
            </div>

            <div class="action-item">
              <label>Produs</label>
              <el-select
                v-model="selectedProduct"
                @change="handleProductSelect"
                @clear="handleClearProduct"
                placeholder="Caută produsul..."
                filterable
                remote
                clearable
                :remote-method="fetchProducts"
                :loading="loadingProducts"
                :disabled="loading"
              >
                <el-option
                  v-for="product in searchProdResults"
                  :key="product._id"
                  :label="product.name"
                  :value="product._id"
                >
                  <template #default>
                  {{ product.name }}
                  <span
                    v-if="product.categorie"
                    :class="getCategoryClass(product.categorie)"
                    style="margin-left: 6px; padding: 2px 6px; border-radius: 50px; color: white; font-size: 11px;"
                  >
                    {{ product.categorie }}
                  </span>
                </template>
                </el-option>
              </el-select>
            </div>

            <div class="action-item">
              <label>{{ $t('gestiune.tipDocument') }}</label>
              <select v-model="selectedDocumentType" class="form-control" :disabled="loading">
                <option
                  v-for="doc in documentTypes"
                  :key="doc.value"
                  :value="doc.value"
                >
                  {{ doc.label }}
                </option>
              </select>
            </div>

            <div class="action-item">
              <label>Număr document</label>
              <input
                type="text"
                v-model="documentNumber"
                class="form-control"
                placeholder="Număr document"
                :disabled="loading"
              />
            </div>
          </div>

          <!-- Al doilea rând: Date și oră -->
          <div class="row" style="margin-top: 10px; align-items: center; flex-wrap: nowrap; max-width: min-content;">
            <div class="action-item full-width" style="flex: 1 1 3%;">
              <label>Date și oră</label>
              <el-date-picker
                type="datetimerange"
                v-model="searchDate"
                format="yyyy-MM-dd HH:mm"
                value-format="yyyy-MM-dd HH:mm:ss"
                class="w-100"
                :picker-options="pickerOptions"
                :name="['date_from', 'date_to']"
                :disabled="loading || stocLaZi"
              ></el-date-picker>
            </div>

            <div class="action-item checkbox-item" style="flex: 1 1 30%; display: flex; align-items: flex-end; margin-left: 15px; max-width: 50px;">
              <label>
                <input type="checkbox" v-model="stocLaZi" @change="handleStocLaZi" :disabled="loading"/>
                Stoc la zi
              </label>
            </div>

            <div class="search-button-reports">
                <button
                    :title="$t('reports.search')"
                    class="btn add mar-rt-10"
                    @click="triggerFilter"
                >
                    <i class="ti-search"></i>
                    <span>{{ $t('general.search') }}</span>
                </button>
            </div>
            <div class="search-button-reports pull-left">
              <div :title="$t('reports.export')" class="btn btn-icon btn-xls-slim" @click="getData(null, 1, 3, 'xls')">{{$t('general.download_xls') }} <img src="/static/img/export_xls.png"></div>
            </div>
            <div class="search-button-reports pull-left">
              <div :title="$t('reports.export')" class="btn btn-icon btn-xls-slim" @click="getData(null, 1, 3, 'pdf')">{{$t('general.download_pdf') }} <img src="/static/img/export_xls.png"></div>
            </div>
          </div>
        </template>
      </div>

      <div class="card mar-to-15 tableInvoice">
          <div class="card-content">
            <div :class="(loading ? 'show-loader' : 'hide-loader')">
              <div class="el-loading-spinner">
                <svg viewBox="25 25 50 50" class="circular">
                  <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
                </svg><!---->
              </div>
              </div>
              <template>
                <div class="data-table-wrapper">
                  <table class="main-table">
                    <thead>
                      <tr>
                        <th rowspan="2" class="align-left">TIP DOCUMENT</th>
                        <th rowspan="2" class="align-center">UM</th>
                        <th colspan="3" class="align-center">CANTITATI</th>
                        <th rowspan="2" class="align-center">PRET UNITAR</th>
                        <th colspan="3" class="align-center">VALOARE</th>
                      </tr>
                      <tr class="subheaders">
                        <th class="align-center">INTRARI</th>
                        <th class="align-center">IESIRI</th>
                        <th class="align-center">STOC</th>
                        <th class="align-center">INTRARI</th>
                        <th class="align-center">IESIRI</th>
                        <th class="align-center">SOLD</th>
                      </tr>
                    </thead>
                    <tbody>
                        <template v-if="groupedTableData.length > 0">
                          <template v-for="prod in groupedTableData">

                            <!-- Când e stocLaZi activ -->
                            <tr v-if="stocLaZi" :key="`stoc-line-${prod.productId}`">
                              <td colspan="1" class="align-right name-product">{{ prod.productName }}</td>
                              <td class="align-right">{{ prod.um }}</td>
                              <td class="align-right">{{ formatNumberWithZecimale(prod.totalIntrare) }}</td>
                              <td class="align-right">{{ formatNumberWithZecimale(prod.totalIesire) }}</td>
                              <td class="align-right">{{ formatNumberWithZecimale(prod.running) }}</td>
                              <td></td>
                              <td class="align-right">{{ formatNumberWithZecimale(prod.valoareIntrare) }}</td>
                              <td class="align-right">{{ formatNumberWithZecimale(prod.valoareIesire) }}</td>
                              <td class="align-right">{{ formatNumberWithZecimale(prod.runningValue) }}</td>
                            </tr>

                            <!-- Când stocLaZi e dezactivat (istoricul complet) -->
                            <template v-else>
                              <tr class="product-name-row" :key="prod.productId">
                                <td :colspan="9" class="product-name-cell">
                                  {{ prod.productName }}
                                </td>
                              </tr>

                              <!-- Istoric -->
                              <tr
                                v-for="(entry, index) in prod.history"
                                :key="`${prod.productId}-${entry.document_id || 'nodoc'}-${index}`"
                                class="history-row"
                                :class="{ 'first-history-row': index === 0 }"
                              >
                                <td>
                                  <div
                                    class="entry-document-number"
                                    :class="{ 'tooltip-wrapper': entry.document_type === 'NIR' }"
                                    @click="goToDocument(entry)"
                                  >
                                    {{ getDocumentNumberLabel(entry) }}

                                    <!-- DOAR NIR -->
                                    <div
                                      v-if="entry.document_type === 'NIR'"
                                      class="custom-tooltip"
                                    >
                                      <div class="tooltip-row">
                                        <span class="tooltip-label">{{ $t('gestiune.stockDocumentType') }}</span>
                                        <span class="tooltip-value">{{ entry.document.tip_document }}</span>
                                      </div>

                                      <div class="tooltip-row">
                                        <span class="tooltip-label">{{ $t('gestiune.nrDocument') }}</span>
                                        <span class="tooltip-value">
                                          {{ entry.document.nirNumber || '-' }}
                                        </span>
                                      </div>

                                      <div class="tooltip-row">
                                        <span class="tooltip-label">{{ $t('gestiune.supplier') }}</span>
                                        <span class="tooltip-value">{{ entry.document.nume_furnizor }} / {{ entry.document.vatNumberFurnizor }}</span>
                                      </div>

                                      <div class="tooltip-divider"></div>

                                      <div class="tooltip-row">
                                        <span class="tooltip-label">{{ $t('gestiune.value') }}</span>
                                        <span class="tooltip-value">
                                          {{ formatNumberWithZecimale(entry.document.total || 0) }}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <div class="entry-date">
                                    {{ formatDate(null, null, entry.date) }}
                                  </div>
                                  <div
                                    style="display: flex; align-items: baseline;"
                                    v-if="entry.document && entry.document.produced_posId"
                                    @click.stop="goToProduced(entry)"
                                    class="produced-label"
                                  >
                                    Vezi unde este folosit
                                  </div>
                                  <div style="display: flex; align-items: baseline;">
                                    <span
                                      @click="goToDocument(entry)"
                                      :class="['tag', toCssClass(entry.document_type === 'BON_FISCAL' && entry.document && entry.document.tip_vanzare ? entry.document.tip_vanzare : entry.document_type)]"
                                      :style="{
                                        backgroundColor: entry.document_type === 'BON_FISCAL' ? '#4CAF50' /* verde */
                                          : randomColor(entry.document_type || '')
                                      }"
                                    >
                                      {{ entry.document_type === 'BON_FISCAL' && entry.document && entry.document.tip_vanzare
                                        ? formatDocumentTypeLabel(entry.document.tip_vanzare)
                                        : formatDocumentTypeLabel(entry.document_type)
                                      }}
                                    </span>
                                  </div>
                                </td>

                                <td>{{ (entry.item && (entry.item.u_m || entry.item.um || entry.item.unitMeasure)) || '' }}</td>
                                <td class="align-right">
                                  {{ (entry.document_type !== 'STOCK_INITIAL' && entry.intrare !== 0)
                                      ? formatNumberWithZecimale(entry.intrare)
                                      : '' }}
                                </td>
                                <td class="align-right">
                                  {{ (entry.document_type !== 'STOCK_INITIAL' && entry.iesire !== 0)
                                      ? formatNumberWithZecimale(entry.iesire)
                                      : '' }}
                                </td>
                                <td class="align-right">
                                  <strong>{{ (entry.stoc_final !== undefined && entry.stoc_final !== null) ? formatNumberWithZecimale(entry.stoc_final) : '-' }}</strong>
                                </td>
                                <td class="align-right">{{ formatNumberWithZecimale(entry.pret, 4) }}</td>
                               <td class="align-right">
                                  {{ (entry.document_type !== 'STOCK_INITIAL' && entry.intrare != 0 && entry.pret)
                                      ? formatNumberWithZecimale(entry.intrare * parseFloat(entry.pret) || 0)
                                      : '' }}
                                </td>
                                <td class="align-right">
                                  {{ (entry.document_type !== 'STOCK_INITIAL' && entry.iesire !== 0 && entry.pret)
                                      ? formatNumberWithZecimale(entry.iesire * parseFloat(entry.pret) || 0)
                                      : '' }}
                                </td>
                                <td class="align-right"><strong>{{ formatNumberWithZecimale(entry.valoare_stoc) }}</strong></td>
                              </tr>

                              <!-- Total produs -->
                              <tr class="history-total-row">
                                <td colspan="2"><strong>Total Produs:</strong></td>
                                <td class="align-right">
                                  <strong>
                                    {{ (prod.totalIntrare !== 0) ? formatNumberWithZecimale(prod.totalIntrare) : '' }}
                                  </strong>
                                </td>
                                <td class="align-right">
                                  <strong>
                                    {{ (prod.totalIesire !== 0) ? formatNumberWithZecimale(prod.totalIesire) : '' }}
                                  </strong>
                                </td>
                                <td class="align-right">
                                  <!-- în loc de prod.stocFinal -->
                                  <strong>{{ prod.running !== null ? formatNumberWithZecimale(prod.running) : '' }}</strong>
                                </td>
                                <td></td>
                                <td class="align-right">
                                  <strong>
                                    {{ (prod.valoareIntrare != null) ? formatNumberWithZecimale(prod.valoareIntrare) : '' }}
                                  </strong>
                                </td>
                                <td class="align-right">
                                  <strong>
                                    {{ (prod.valoareIesire != null) ? formatNumberWithZecimale(prod.valoareIesire) : '' }}
                                  </strong>
                                </td>
                                <td class="align-right">
                                  <!-- în loc de prod.valoareStoc -->
                                  <strong>{{ prod.runningValue !== null ? formatNumberWithZecimale(prod.runningValue) : '' }}</strong>
                                </td>
                              </tr>
                            </template>
                          </template>
                        </template>
                        <tr v-else>
                          <td :colspan="9" style="text-align: center; color: #909399; padding: 20px;">
                            {{ $t('gestiune.noData') }}
                          </td>
                        </tr>
                        <tr class="general-total-row" v-if="groupedTableData.length > 0">
                          <td colspan="6" style="text-align: right;">
                            <strong>
                              TOTAL <span v-if="selectedGestiuneObject"> <b>{{ selectedGestiuneObject.name }}</b></span>:
                            </strong>
                          </td>
                          <td class="align-right">
                            <strong>
                              {{ totalGeneral.valoareIntrare !== 0 ? formatNumberWithZecimale(totalGeneral.valoareIntrare) : '' }}
                            </strong>
                          </td>
                          <td class="align-right">
                            <strong>
                              {{ totalGeneral.valoareIesire !== 0 ? formatNumberWithZecimale(totalGeneral.valoareIesire) : '' }}
                            </strong>
                          </td>
                          <td class="align-right">
                            <strong>
                              {{ totalGeneral.valoareStoc !== 0 ? formatNumberWithZecimale(totalGeneral.valoareStoc) : '' }}
                            </strong>
                          </td>
                        </tr>
                    </tbody>
                  </table>
                </div>
                <el-pagination
                  layout="prev, pager, next"
                  :total="totalItems"
                  :page-size="pageSize"
                  :current-page="currentPage"
                  @current-change="handlePageChange"
                  style="margin-top: 15px; text-align: right;"
                ></el-pagination>
              </template>
          </div>
      </div>
    </div>
</template>

<script>
// import GeneralTopBar from 'src/components/UIComponents/Inputs/generalTopBar.vue'
import swal from 'sweetalert2'

export default {
   // components: {
  //   GeneralTopBar
  // },
  data () {
    const end = new Date()
    const start = new Date()
    start.setDate(end.getDate() - 29)
    start.setHours(0, 0, 0, 0)
    end.setHours(23, 59, 59, 999)

    return {
      stocLaZi: false,
      loading: false,
      loadingTimeout: null,
      selectedProduct: null,
      selectedDocumentType: '',
      loadingProducts: false,
      selectedCategory: '',
      documentNumber: '',
      tableData: [],
      searchDate: [start, end],
      filter: {
        series: ''
      },
      totalGeneral: {
        valoareIntrare: 0,
        valoareIesire: 0,
        valoareStoc: 0
      },
      categories: [
        { value: '', label: 'Selectează categoria' },
        { value: 'Marfa', label: 'Marfa' },
        { value: 'Materii prime', label: 'Materii prime' },
        { value: 'Materiale consumabile', label: 'Materiale consumabile' },
        { value: 'Semifabricate (Reteta)', label: 'Semifabricate (Reteta)' },
        { value: 'Produs finit (Reteta)', label: 'Produs finit (Reteta)' },
        { value: 'Produs rezidual', label: 'Produs rezidual' },
        { value: 'Ambalaje', label: 'Ambalaje' },
        { value: 'Obiecte de inventar', label: 'Obiecte de inventar' },
        { value: 'fara_categorie', label: 'Fara categorie' }
      ],
      documentTypes: [
        { value: '', label: 'Selectează tipul de document' },
        { value: 'NIR', label: 'NIR' },
        { value: 'BON_TRANSFER', label: 'Bon de transfer' },
        { value: 'BON_PRODUCTIE', label: 'Bon de producție' },
        { value: 'BON_CONSUM', label: 'Bon de consum' },
        { value: 'BON_FISCAL', label: 'Bon fiscal' },
        { value: 'AVIZ', label: 'Aviz' }
      ],
      totalItems: 0,
      currentPage: 1,
      pageSize: 20,
      searchProdResults: [],
      groupedTableData: [],
      flattenedHistory: [],
      searchTerm: null,
      searchTimeout: null,
      selectedGestiune: null,
      selectedLocation: null,
      pickerOptions: {
        shortcuts: [
          {
            text: 'Astăzi',
            onClick (picker) {
              const start = new Date()
              start.setHours(0, 0, 0, 0)
              const end = new Date()
              end.setHours(23, 59, 59, 999)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: 'Ieri',
            onClick (picker) {
              const start = new Date()
              start.setDate(start.getDate() - 1)
              start.setHours(0, 0, 0, 0)
              const end = new Date()
              end.setDate(end.getDate() - 1)
              end.setHours(23, 59, 59, 999)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: 'Ultimele 7 zile',
            onClick (picker) {
              const end = new Date()
              const start = new Date()
              start.setDate(end.getDate() - 6)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: 'Ultimele 30 de zile',
            onClick (picker) {
              const end = new Date()
              const start = new Date()
              start.setDate(end.getDate() - 29)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: 'Luna curentă',
            onClick (picker) {
              const start = new Date()
              start.setDate(1)
              start.setHours(0, 0, 0, 0)
              const end = new Date()
              end.setHours(23, 59, 59, 999)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: 'Luna trecută',
            onClick (picker) {
              const date = new Date()
              const start = new Date(date.getFullYear(), date.getMonth() - 1, 1)
              const end = new Date(date.getFullYear(), date.getMonth(), 0)
              end.setHours(23, 59, 59, 999)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: 'Anul curent',
            onClick (picker) {
              const start = new Date(new Date().getFullYear(), 0, 1)
              const end = new Date()
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: 'Anul trecut',
            onClick (picker) {
              const year = new Date().getFullYear() - 1
              const start = new Date(year, 0, 1)
              const end = new Date(year, 11, 31)
              end.setHours(23, 59, 59, 999)
              picker.$emit('pick', [start, end])
            }
          }
        ],
        onPick ({ maxDate, minDate }) {
          if (minDate && !maxDate) {
            minDate.setHours(0, 0, 0, 0)
          }
          if (maxDate) {
            maxDate.setHours(23, 59, 59, 999)
          }
        }
      }
    }
  },
  props: {
    gestiuneOptions: {
      type: Array,
      required: true
    },
    groupedLocations: {
      type: Array,
      required: true
    },
    extraProp: { type: Boolean, required: false }
  },
  computed: {
    filteredGestiuni () {
      if (!this.selectedLocation) return []
      const result = this.gestiuneOptions.filter(
        (g) => g.externalLocationId === this.selectedLocation.true_id
      )
      return result
    },
    selectedGestiuneObject () {
      return this.gestiuneOptions.find(
        (g) => g.id === this.selectedGestiune
      )
    }
  },
  watch: {
    selectedLocation (newLocation) {
      this.resetTable()

      this.selectedGestiune = null
      this.selectedProduct = null
      this.tableData = []
      if (this.filteredGestiuni && this.filteredGestiuni.length) {
        this.selectedGestiune = this.filteredGestiuni[0].id
      }
      this.selectedVatNumber = newLocation.inv_fiscal_code
    },
    selectedGestiune () {
      this.resetTable()
    },
    selectedCategory () {
      this.resetTable()

      this.searchProdResults = []
      this.selectedProduct = null
    },
    selectedDocumentType () {
      this.resetTable()
    },
    documentNumber () {
      this.resetTable()
    },
    groupedLocations (newVal) {
      this.firstLoad()
    },
    gestiuneOptions (newVal) {
      this.firstLoad()
    },
    stocLaZi (val) {
      this.resetTable()

      this.pageSize = val ? 50 : 3
    },
    searchDate () {
      this.resetTable()
    }
  },
  created () {
    this.companyID = this.$session.get('selectedCompany')
  },
  mounted () {
    this.firstLoad()

    console.log('ss', this.extraProp)
    if (this.extraProp) {
      this.stocLaZi = true
      this.handleStocLaZi()
    }
  },
  methods: {
    resetTable () {
      this.groupedTableData = []
      this.totalItems = 0
      this.currentPage = 1
      this.pageSize = 20
      this.totalGeneral = { valoareIntrare: 0, valoareIesire: 0, valoareStoc: 0 }
    },
    getCategoryClass (categorie) {
      if (!categorie) return ''

      switch (categorie.toLowerCase().replace(/\s+/g, '-')) {
        case 'marfa': return 'marfa'
        case 'semifabricate-(reteta)': return 'semifabricate'
        case 'materii-prime': return 'materii-prime'
        case 'produs-finit-(reteta)': return 'produs-finit'
        default: return ''
      }
    },
    firstLoad () {
      if (this.groupedLocations && this.groupedLocations.length) {
        this.selectedLocation = this.groupedLocations[0].locations[0]
      }
    },
    handleStocLaZi () {
      this.resetTable()

      if (this.stocLaZi) {
        const today = new Date()
        const start = new Date(today.setHours(0, 0, 0, 0))
        const end = new Date(today.setHours(23, 59, 59, 999))

        this.searchDate = [start, end]
      }
    },
    async downloadDocument (format) {
      const externalCompanyId = this.companyID
      if (!externalCompanyId) {
        alert('Missing company ID.')
        return
      }

      // URL-ul cu doar doi parametri: export + externalCompanyId
      const url = `${process.env.API_BASE_URL_GESTIUNE}:4443/export?export=${format}&externalCompanyId=${externalCompanyId}`

      try {
        const response = await fetch(url)
        if (response.ok) {
          const blob = await response.blob()
          const link = document.createElement('a')
          link.href = URL.createObjectURL(blob)
          link.download = `document.${format.toLowerCase()}` // nume fișier simplu
          link.click()
          URL.revokeObjectURL(link.href)
        } else {
          console.error('Error downloading document:', response.statusText)
        }
      } catch (error) {
        console.error('Error fetching document:', error)
      }
    },
    handlePageChange (page) {
      this.currentPage = page
      const size = this.stocLaZi ? 50 : this.pageSize
      this.getData(null, this.currentPage, size)
    },
    toCssClass (str) {
      if (!str) return ''
      return str
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '')
    },
    randomColor (seed) {
      // O metodă simplă să generezi o culoare consistentă pentru un seed text
      let hash = 0
      for (let i = 0; i < seed.length; i++) {
        hash = seed.charCodeAt(i) + ((hash << 5) - hash)
      }
      const c = (hash & 0x00FFFFFF).toString(16).toUpperCase()
      return '#' + '00000'.substring(0, 6 - c.length) + c
    },
    formatDocumentTypeLabel (str) {
      if (!str) return '-'
      return str
        .replace(/_/g, ' ')               // underscore în spațiu
        .toLowerCase()
        .split(' ')                      // sparge în cuvinte
        .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // prima literă mare
        .join(' ')
    },
    formatNumberWithZecimale (value, decimals = 2) {
      if (value === undefined || value === null || isNaN(value)) return '-'
      return parseFloat(value).toFixed(decimals)
    },
    formatNumber (value) {
      if (value === null || value === undefined) return ''
      // Rotunjim la întreg
      return Math.round(value).toString()
    },
    formatDate (row, column, cellValue) {
      if (!cellValue) return ''
      const d = new Date(cellValue)
      return d.toLocaleString('ro-RO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },
    triggerFilter () {
      this.resetTable()

      this.tableData = []
      this.getData()
    },
    handleProductSelect (product) {
      this.resetTable()

      this.selectedProduct = product
    },
    handleClearProduct () {
      this.resetTable()

      this.selectedProduct = null
    },
    async fetchProducts (searchTerm) {
      if (!searchTerm || searchTerm.length < 2) {
        this.searchProdResults = []
        return
      }

      this.loadingProducts = true

      try {
        let url = `${process.env.API_BASE_URL_GESTIUNE}:4443/products?vatNumber=${this.selectedVatNumber}&name=${encodeURIComponent(searchTerm)}&externalCompanyId=${this.companyID}`

        if (this.selectedCategory) {
          url += `&categorie=${encodeURIComponent(this.selectedCategory)}`
        }

        const response = await fetch(url)
        const data = await response.json()

        if (Array.isArray(data.products)) {
          this.searchProdResults = [...data.products]
        } else {
          this.searchProdResults = []
        }
      } catch (error) {
        console.error('Error fetching products:', error)
        this.searchProdResults = []
      } finally {
        this.loadingProducts = false
      }
    },
    async getData (date = null, page = 1, pageSize = 3, exportFormat = null) {
      const externalCompanyId = this.companyID

      if (!externalCompanyId || !this.selectedGestiune || !this.selectedLocation) {
        swal({
          type: 'warning',
          title: this.selectedGestiune ? 'Select Location' : 'Select Gestiune',
          text: !this.selectedGestiune
              ? 'Trebuie să selectați o gestiune pentru a continua.'
              : 'Trebuie să selectați o locație pentru a continua.',
          confirmButtonText: 'OK'
        })
        return
      }

      if (this.stocLaZi) {
        pageSize =
        this.pageSize = 50
      }

      clearTimeout(this.loadingTimeout)
      this.loading = true

      let url = `${process.env.API_BASE_URL_GESTIUNE}:4443/stock_history_advanced_v2?externalCompanyId=${externalCompanyId}`
      if (date) url += `&date=${date}`
      if (this.selectedGestiune) url += `&gestiune=${this.selectedGestiune}`
      if (this.selectedLocation) url += `&externalLocationId=${this.selectedLocation.true_id}`

      if (this.selectedCategory) {
        url += `&categorie=${encodeURIComponent(this.selectedCategory)}`
      }

      if (this.selectedDocumentType) {
        url += `&document_type=${encodeURIComponent(this.selectedDocumentType)}`
      }

      url += `&page=${page}&limit=${pageSize}`

      if (exportFormat) {
        url += `&export=${exportFormat}`
      }

      if (
        this.searchDate &&
        this.searchDate.length >= 2 &&
        this.searchDate[0] &&
        this.searchDate[1]
      ) {
        const startISO = new Date(this.searchDate[0])
        const endISO = new Date(this.searchDate[1])
        url += `&startDate=${encodeURIComponent(startISO)}&endDate=${encodeURIComponent(endISO)}`
      }

      if (this.selectedProduct) {
        url += `&product_id=${this.selectedProduct}`
      }

      try {
        const response = await fetch(url)

        if (exportFormat) { // dacă e export
          if (!this.groupedTableData || this.groupedTableData.length === 0) {
            swal({
              type: 'warning',
              title: 'Nu există date',
              text: 'Nu există date pentru export în perioada și filtrele selectate.',
              icon: 'warning',
              confirmButtonText: 'OK'
            })
            return
          }

          swal({
            type: 'warning',
            title: 'Se procesează exportul...',
            text: 'În funcție de perioadă și filtrele aplicate, exportul poate dura. Vă rugăm să așteptați...',
            allowOutsideClick: false,
            showConfirmButton: false,
            didOpen: () => {
              swal.showLoading()
            }
          })
          try {
            const response = await fetch(url)

            if (response.ok) {
              const blob = await response.blob()  // fișier binar
              const link = document.createElement('a')
              link.href = URL.createObjectURL(blob)
              link.download = `stock_history.${exportFormat.toLowerCase()}`
              link.click()
              URL.revokeObjectURL(link.href)
            } else {
              console.error('Error downloading export:', response.statusText)
              swal({
                title: 'Eroare',
                text: `Eroare la descărcarea fișierului: ${response.statusText}`,
                type: 'error',
                timer: 3000,
                showConfirmButton: false
              })
            }
          } catch (err) {
            console.error('Eroare fetch export:', err)
            swal({
              title: 'Eroare',
              text: 'A apărut o eroare la descărcarea fișierului.',
              type: 'error',
              timer: 3000,
              showConfirmButton: false
            })
          } finally {
            swal.close()
          }
          return
        }

        // Dacă nu e export, atunci parsează JSON normal
        const data = await response.json()
        this.totalItems = data.total || 0
        this.currentPage = data.currentPage || page
        this.pageSize = data.pageSize || pageSize

        if (data.status === true && Array.isArray(data.products) && data.products.length === 0) {
          this.groupedTableData = []
          return
        }

        let products = []
        if (data.products && Array.isArray(data.products)) {
          products = data.products
        } else if (data.history && data.product_id) {
          products = [data]
        }

        const processed = products.map(prod => {
          const firstHistory = prod.history && prod.history.length > 0 ? prod.history[0] : null
          const firstItem = firstHistory && firstHistory.item ? firstHistory.item : {}

          const history = Array.isArray(prod.history) ? prod.history : []

          // totaluri
          const totalIntrare = history
            .filter(e => e.document_type !== 'STOCK_INITIAL') // doar calcul, nu le ascunde
            .reduce((sum, e) => sum + (parseFloat(e.intrare) || 0), 0)

          const totalIesire = history
            .filter(e => e.document_type !== 'STOCK_INITIAL')
            .reduce((sum, e) => sum + (parseFloat(e.iesire) || 0), 0)

          const totalValoareIntrare = history
            .filter(e => e.document_type !== 'STOCK_INITIAL')
            .reduce((sum, e) => {
              const cant = parseFloat(e.intrare) || 0
              const pret = parseFloat(e.pret) || 0
              return sum + cant * pret
            }, 0)

          const totalValoareIesire = history
            .filter(e => e.document_type !== 'STOCK_INITIAL')
            .reduce((sum, e) => {
              const cant = parseFloat(e.iesire) || 0
              const pret = parseFloat(e.pret) || 0
              return sum + cant * pret
            }, 0)

          const lastEntry = history.length > 0 ? history[history.length - 1] : {}

          const hasStockInitial = history.some(e => e.document_type === 'STOCK_INITIAL')

          return {
            productId: prod.product_id,
            productName: prod.denumire_produs_sau_serviciu || firstItem.product_naame || 'N/A',
            um: (firstItem.um || firstItem.product_um || 'N/A').toUpperCase(),
            stocMinim: prod.productDetails && prod.productDetails.stocMinim ? prod.productDetails.stocMinim : 0,
            totalQuantity: totalIntrare - totalIesire,
            totalIntrare,
            totalIesire,
            valoareIntrare: totalValoareIntrare > 0 ? totalValoareIntrare : null,
            valoareIesire: totalValoareIesire > 0 ? totalValoareIesire : null,
            stocFinal: lastEntry && lastEntry.stoc_final != null ? lastEntry.stoc_final : null,
            running: lastEntry && lastEntry.running != null ? lastEntry.running : 0,
            runningValue: lastEntry && lastEntry.runningValue != null ? lastEntry.runningValue : 0,
            valoareStoc: lastEntry && lastEntry.valoare_stoc != null ? lastEntry.valoare_stoc : null,
            hasStockInitial,
            history: prod.history && Array.isArray(prod.history)
              ? prod.history
                  .map(h => {
                    const item = h.item || {}
                    const document = h.document || {}
                    return {
                      ...h,
                      item: {
                        ...item,
                        id: item.product_id || item.posId || null
                      },
                      document
                    }
                  })
              : []
          }
        })

        this.groupedTableData = processed

        this.totalGeneral = {
          valoareIntrare: processed
            .filter(e => e.document_type !== 'STOCK_INITIAL')
            .reduce((sum, p) => sum + (p.valoareIntrare || 0), 0),
          valoareIesire: processed
            .filter(e => e.document_type !== 'STOCK_INITIAL')
            .reduce((sum, p) => sum + (p.valoareIesire || 0), 0),
          valoareStoc: processed.reduce((sum, p) => sum + (p.runningValue || 0), 0)
        }
      } catch (err) {
        console.error('Eroare fetch:', err)
      } finally {
        this.loading = false
      }
    },
    goToProduced (entry) {
      if (!entry || !entry.document || !entry.document.produced_posId) return

      const producedId = entry.document.produced_posId
      const vatNumber = entry.document.company.vatNumber || ''

      this.$router.push({
        path: `/management/nomenclator-products/edit/${producedId}/${vatNumber}`,
        query: {
          highlight: entry.item.product_id || entry.document.produced_posId
        }
      })
    },
    goToDocument (entry) {
      try {
        if (!entry || !entry.document_type) {
          return false
        }

        const type = entry.document_type
        const doc = entry.document || {}

        const id = entry.document_id || doc._id

        if (!id) {
          return false
        }

        // NIR
        if (type === 'NIR') {
          if (!doc.nirNumber) return false

          return this.$router.push({
            path: `/management/entries/edit/${id}`,
            query: {
              nirNumber: doc.nirNumber,
              data_document: doc.data_document,
              vatNumberFurnizor: doc.vatNumberFurnizor,
              series: doc.series
            }
          })
        }

        if (type === 'BON_TRANSFER') {
          if (!doc.series || !doc.documentNumber) return false

          return this.$router.push({
            path: `/management/transfer/edit/${doc.series}/${doc.documentNumber}`
          })
        }

        if (type === 'BON_PRODUCTIE') {
          if (!doc.series || !doc.documentNumber) return false

          return this.$router.push({
            path: `/management/bon-product/edit/${doc.series}/${doc.documentNumber}`
          })
        }

        if (type === 'BON_FISCAL') {
          if (!doc._id) return false

          return this.$router.push({
            name: 'ManagementBonFiscalView',
            params: { id: doc._id }
          })
        }

        if (type === 'AVIZ') {
          if (!doc.series || !doc.invoiceNumber) return false

          const invoiceId = `${doc.series}-${doc.invoiceNumber}`

          return this.$router.push({
            path: `/management/outputs/${invoiceId}`,
            query: { edit: 'true' }
          })
        }

        if (type === 'BON_CASARE') {
          if (!doc.series || !doc.documentNumber) return false

          return this.$router.push({
            path: `/management/wasted/edit/${doc.series}/${doc.documentNumber}`
          })
        }

        if (type === 'BON_CONSUM') {
          if (!doc.series || !doc.documentNumber) return false

          return this.$router.push({
            path: `/management/stocks/edit/${doc.series}/${doc.documentNumber}`
          })
        }

        return false
      } catch (error) {
        return false
      }
    },
    getDocumentNumberLabel (entry) {
      const doc = entry.document || {}
      const type = entry.document_type

      if (type === 'NIR') {
        if (doc.series && doc.nirNumber) {
          return `${doc.series}-${doc.nirNumber}`
        }
        return doc.nirNumber || ''
      }

      if (['BON_TRANSFER', 'BON_PRODUCTIE', 'BON_CONSUM'].includes(type)) {
        if (doc.documentNumber) {
          return `${doc.series}-${doc.documentNumber}`
        }
        return ''
      }

      if (type === 'BON_FISCAL') {
        if (doc.nr) {
          return `NR: ${doc.nr}`
        }
        return ''
      }

      if (type === 'AVIZ') {
        if (doc.series && doc.invoiceNumber) {
          return `${doc.series}-${doc.invoiceNumber}`
        }
        return doc.invoiceNumber || ''
      }

      if (type === 'INVENTAR') {
        if (doc.series && doc.number) {
          return `${doc.series}-${doc.number}`
        }
        return doc.number || ''
      }

      return ''
    },
    async downloadInvoice (row) {
      const series = row.series
      const invoiceNumber = row.invoiceNumber

      if (!series || !invoiceNumber) {
        alert('Missing series or invoice number.')
        return
      }

      const url = `${process.env.API_BASE_URL_GESTIUNE}:4443/invoice/${series}-${invoiceNumber}/pdf`

      try {
        const response = await fetch(url)
        if (response.ok) {
          const blob = await response.blob()
          const link = document.createElement('a')
          link.href = URL.createObjectURL(blob)
          link.download = `${series}-${invoiceNumber}.pdf`
          link.click()
          URL.revokeObjectURL(link.href)
        } else {
          console.error('Error downloading invoice:', response.statusText)
        }
      } catch (error) {
        console.error('Error fetching invoice:', error)
      }
    }
  }
}
</script>

<style scoped>
.data-table-wrapper {
  border-radius: 6px;
}
.main-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: #E4E8F9;
}
td.name-product {
    padding: 20px !important;
    color: #674BD2;
    font-weight: 600;
    font-size: 16px;
    padding-bottom: 10px !important;
    padding-left: 10px !important;
    text-align: left !important;
}
/* Al doilea rând din thead (subheader) trebuie și el să fie sticky, sub primul */
.main-table thead tr:nth-child(2) th {
  top: 32px; /* ajustează dacă e nevoie, în funcție de înălțimea primului rând */
  z-index: 1;
  background-color: #E4E8F9;
}
.main-table td,
.main-table th {
  background-color: #E4E8F9;
  font-weight: 500;
  text-align: center;     /* centrează conținutul orizontal */
  vertical-align: middle; /* centrează conținutul vertical */
}

.product-name-cell {
  text-align: left !important;
}
.product-name-row:not(:first-child) {
  padding-top: 15px;
  margin-top: 15px;
  border-top: 2px solid #000;
}
/* Dacă vrei și conținutul din div-uri să fie centrat pe orizontală */
.history-row td > div {
  display: flex;
}
.main-table tbody tr {
  border-bottom: 1px solid #d0d4e359;
}
.main-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.align-center {
  text-align: center;
  vertical-align: middle;
}
.align-left {
  text-align: left;
}
.search-button-reports button.add{
  background-color: #20BFAB !important;
  border: 1px solid #20BFAB !important;
}
.main-table th,
.main-table td {
  padding: 6px 8px;
}
thead tr:nth-child(2) th{
  border-radius: 0px !important;
}
tbody td{
  background-color: #fff !important;
}
.align-right {
  text-align: right;
}
.product-name-row {
  text-align: left;
}
.product-row {
  background-color: #f0f0f0;
}

.history-table-wrapper {
  overflow-x: auto;
}
.first-history-row td {
  padding-top: 20px;
}
.history-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
td.product-name-cell {
  padding: 20px !important;
  color: #674BD2;
  font-weight: 600;
  font-size: 16px;
  padding-bottom: 10px !important;
  padding-left: 10px !important;
}
strong {
  color: #25396F;
}
.history-table th,
.history-table td {
  border: 1px solid #ccc;
  padding: 4px 6px;
}
tr.history-total-row strong {
    font-size: 16px;
}
.general-total-row {
  border-top: 3px solid #000;
  font-weight: bold;
  background-color: #fafafa;
}
.action-bar {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.action-bar .row {
  margin: 0px;
  align-items: center;
}
.row {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.action-item {
  flex: 1 1 180px; /* minim 180px, se extinde dacă e spațiu */
  min-width: 160px;
}

.full-width {
  flex: 1 1 100%;
}

.checkbox-item {
  flex: 0 0 auto;
  margin-top: 4px; /* puțin spațiu sus */
}

.general-total-row td {
  padding-top: 20px;
  padding-bottom: 20px;
  font-size: 18px;
}
.tag {
  padding: 0px 8px;
  border-radius: 50px;
  color: white !important;
  font-weight: bold;
  font-size: 11px;
  display: inline-block;
  min-width: 70px;
  text-align: center;
  line-height: 24px;
  margin-left: 0px;
  width: 110px;
  cursor: pointer;
}
.entry-document-number {
    color: orange;
    font-size: 16px;
    text-transform: uppercase;
    font-weight: 600;
    margin-bottom: 5px;
    cursor: pointer;
}
.tag.bon_transfer {
  background-color: rgb(58, 183, 205) !important; /* albastru */
}
span.tag.nir {
  background-color: orange !important;
}
span.tag.bon_consum {
  background-color: #674BD2 !important;
}
span.stoc_initial {
  background-color: blue !important; /* portocaliu */
}
.tag.inventar {
  background-color: #ea7867 !important; /* galben */
}
span.tag.bon_productie {
  background-color: #262945 !important;
}
span.tag.bon_fiscal {
  background-color: #20bfab !important;
}
span.tag.aviz {
 background-color: rgb(76, 175, 80) !important;
}
.marfa {
  background-color: orange;
  color: #fff;
}
.semifabricate {
  background-color: #219653;
  color: #fff;
}
.materii-prime {
  background-color: #2f80ed;
  color: #fff;
}
.produs-finit {
  background-color: #674BD2;
  color: #fff;
}
@media (max-width: 768px) {
  .main-table {
    font-size: 13px;
  }

  .main-table th,
  .main-table td {
    padding: 8px 6px;
  }
}
.tableInvoice .el-table .cell {
  height: 90px;
}
.action-buttons {
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  flex-direction: column;
}

.left-buttons {
  display: flex;
  gap: 15px;
}

.disabled {
  pointer-events: none;
  opacity: 0.5;
}
.tooltip-wrapper {
  position: relative;
  display: inline-block;
  cursor: pointer;
}

/* Tooltip box */
.custom-tooltip {
  position: absolute;
  bottom: calc(100% + 10px);
  left: 0;

  min-width: 220px;
  max-width: 280px;

  background: linear-gradient(145deg, #1e1f23, #15161a);
  color: #eaeaea;

  padding: 10px 12px;
  border-radius: 10px;

  font-size: 12px;
  line-height: 1.4;

  opacity: 0;
  visibility: hidden;
  transform: translateY(6px) scale(0.98);
  transition: all 0.18s ease;

  box-shadow:
    0 10px 25px rgba(0, 0, 0, 0.35),
    0 2px 6px rgba(0, 0, 0, 0.2);

  z-index: 999;
  pointer-events: none;
}

/* Hover effect */
.tooltip-wrapper:hover .custom-tooltip {
  opacity: 1;
  visibility: visible;
  transform: translateY(0) scale(1);
}

/* Arrow */
.custom-tooltip::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 12px;

  border-width: 6px;
  border-style: solid;
  border-color: #15161a transparent transparent transparent;
}

/* Rows inside tooltip */
.tooltip-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin: 3px 0;
}

/* Label */
.tooltip-label {
  color: #9aa0a6;
  font-weight: 500;
}

/* Value */
.tooltip-value {
  color: #ffffff;
  font-weight: 600;
  text-align: right;
}

.produced-label {
  font-size: 11px;
  background-color: #4caf50;
  border-radius: 4px;
  color: #fff;
  padding: 2px 6px;
  width: fit-content;
  cursor: pointer;
  font-weight: 600;
  margin-bottom: 2px;
}

/* subtle separator */
.tooltip-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  margin: 6px 0;
}
.left-buttons button{
  display: flex;
  align-items: center;
  padding-top: 10px;
}
.entry-date {
  margin-bottom: 3px;
  margin-top: 3px;
  font-size: 14px;
  font-weight: 600;
}
.left-buttons el-button {
  margin-right: 10px; /* Spațiere între butoane */
}

.right-button {
  margin-left: auto; /* Asigură că butonul "Editare Multipla" se aliniază la dreapta */
}
.tag {
  color: white;
  font-weight: bold;
  text-transform: uppercase;
  display: inline-block;
}
.tag-bon-fiscal {
  background-color: #2ecc71; /* verde */
}
.tag-nir {
  background-color: #e67e22; /* portocaliu */
}
.button-group {
  display: flex; /* Folosește flexbox pentru alinierea orizontală */
  justify-content: flex-end; /* Aliniază butoanele la dreapta */
}
.button-group .btn {
    margin-right: 10px; /* Adaugă un spațiu între butoane */
}
.button-group .btn:last-child {
    margin-right: 0; /* Elimină marginea dreaptă pentru ultimul buton */
}
.v-select-wrapper {
  position: relative;
}
.v-select-loader {
  position: absolute;
  right: 25px;
  top: 50%;
  transform: translateY(-50%);
  border: 3px solid #e0e0e0;     /* mai deschis */
  border-top: 3px solid #007bff;  /* mai contrastant */
  border-radius: 50%;
  width: 24px;                    /* mai mare */
  height: 24px;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
.info-note {
  background: #f0f7ff;
  border-left: 4px solid #2b6cb0;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 12px;
  font-size: 14px;
  color: #2d3748;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  width: 100%;
}

.info-note .info-title {
  display: block;
  font-weight: 600;
  color: #2b6cb0;
  margin-bottom: 4px;
}
.el-pagination {
  text-align: center !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Management/Reports/NirsReport.vue